import { Controller } from "@hotwired/stimulus"

// Admin announcement form: live preview strip (text, link, color, dismiss
// button), schedule inputs shown in the admin's local timezone and submitted
// as UTC via hidden fields, and a derived status chip ("Live now" /
// "Scheduled …" / "Ended …") with a detail line.
export default class extends Controller {
  static targets = [
    "contentInput",
    "urlInput",
    "urlTextInput",
    "urlTextWrapper",
    "dismissibleInput",
    "previewBanner",
    "previewContent",
    "previewLink",
    "previewClose",
    "colorName",
    "startsLocalInput",
    "startsHidden",
    "endsLocalInput",
    "endsHidden",
    "statusHint",
    "statusHintIcon",
    "statusHintText",
    "statusHintDetail",
    "timeZoneHint"
  ]

  connect() {
    this.timeZoneHintTarget.textContent = Intl.DateTimeFormat().resolvedOptions().timeZone
    this.syncLocalFromHidden(this.startsHiddenTarget, this.startsLocalInputTarget)
    this.syncLocalFromHidden(this.endsHiddenTarget, this.endsLocalInputTarget)
    this.refresh()
  }

  refresh() {
    this.updatePreview()
    this.updateStatusHint()
  }

  startsChanged() {
    this.applyLocal(this.startsLocalInputTarget, this.startsHiddenTarget)
    this.refresh()
  }

  endsChanged() {
    this.applyLocal(this.endsLocalInputTarget, this.endsHiddenTarget)
    this.refresh()
  }

  startsNow(event) {
    event.preventDefault()
    this.setLocal(this.startsLocalInputTarget, this.startsHiddenTarget, new Date())
    this.refresh()
  }

  clearStarts(event) {
    event.preventDefault()
    this.clear(this.startsLocalInputTarget, this.startsHiddenTarget)
    this.refresh()
  }

  endsInDays(event) {
    event.preventDefault()
    const base = this.dateFromHidden(this.startsHiddenTarget.value) || new Date()
    base.setDate(base.getDate() + Number(event.currentTarget.dataset.days || 7))
    this.setLocal(this.endsLocalInputTarget, this.endsHiddenTarget, base)
    this.refresh()
  }

  clearEnds(event) {
    event.preventDefault()
    this.clear(this.endsLocalInputTarget, this.endsHiddenTarget)
    this.refresh()
  }

  updatePreview() {
    const content = this.contentInputTarget.value.trim()
    this.previewContentTarget.textContent = content || "Your announcement text…"
    this.previewContentTarget.classList.toggle("italic", content === "")

    const url = this.urlInputTarget.value.trim()
    this.urlTextWrapperTarget.classList.toggle("hidden", url === "")
    this.previewLinkTarget.classList.toggle("hidden", url === "")
    this.previewLinkTarget.textContent = this.urlTextInputTarget.value.trim() || "Learn more"

    const swatch = this.element.querySelector("input[data-announcement-form-color]:checked")
    if (swatch) {
      this.previewBannerTarget.style.backgroundColor = swatch.value
      this.previewBannerTarget.style.color = swatch.dataset.textColor
      this.colorNameTarget.textContent = swatch.dataset.name
    }

    this.previewCloseTarget.classList.toggle("hidden", !this.dismissibleInputTarget.checked)
  }

  updateStatusHint() {
    const status = this.element.querySelector('input[name="announcement[status]"]:checked')?.value
    if (status !== "active") {
      this.setStatusHint({
        label: "Draft",
        icon: "mdi-pencil-outline",
        chipClasses: "bg-slate-700 text-slate-300",
        detail: "Not visible on the site."
      })
      return
    }

    const now = new Date()
    const starts = this.dateFromHidden(this.startsHiddenTarget.value)
    const ends = this.dateFromHidden(this.endsHiddenTarget.value)

    if (starts && starts > now) {
      this.setStatusHint({
        label: "Scheduled",
        icon: "mdi-clock-outline",
        chipClasses: "bg-amber-500/10 text-amber-300",
        detail: `Goes live ${this.formatLocal(starts)}.`
      })
    } else if (ends && ends < now) {
      this.setStatusHint({
        label: "Ended",
        icon: "mdi-alert-circle-outline",
        chipClasses: "bg-red-500/10 text-red-300",
        detail: `No longer shown after ${this.formatLocal(ends)}.`
      })
    } else {
      this.setStatusHint({
        label: "Live now",
        icon: "mdi-check-circle-outline",
        chipClasses: "bg-green-500/10 text-green-300",
        detail: "Visible on the site."
      })
    }
  }

  setStatusHint({ label, icon, chipClasses, detail }) {
    this.statusHintTarget.className = `chip ${chipClasses}`
    this.statusHintIconTarget.setAttribute("icon", icon)
    this.statusHintTextTarget.textContent = label
    this.statusHintDetailTarget.textContent = detail
  }

  // Hidden fields carry "YYYY-MM-DD HH:MM:SS" in UTC (the app runs in UTC).
  dateFromHidden(value) {
    if (!value) return null
    const date = new Date(`${value.trim().replace(" ", "T")}Z`)
    return isNaN(date) ? null : date
  }

  applyLocal(localInput, hidden) {
    hidden.value = localInput.value ? this.toUtcString(new Date(localInput.value)) : ""
  }

  setLocal(localInput, hidden, date) {
    localInput.value = this.toLocalValue(date)
    hidden.value = this.toUtcString(date)
  }

  clear(localInput, hidden) {
    localInput.value = ""
    hidden.value = ""
  }

  syncLocalFromHidden(hidden, localInput) {
    const date = this.dateFromHidden(hidden.value)
    localInput.value = date ? this.toLocalValue(date) : ""
  }

  toUtcString(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:00`
  }

  toLocalValue(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  formatLocal(date) {
    return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
  }
}
